import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Button from '../Button';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';
import { getGoogleRedirectLink } from 'utils';
import useGeoData from 'hooks/useGeoData';

const useStyles = makeStyles((theme) => ({
  section: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    paddingBottom: theme.spacing(16),
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(16),
    },
  },
  container: {
    maxWidth: '1280px',
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    animation: '$fadeInUp 0.8s ease-out',
  },
  title: {
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#121212',
    textAlign: 'left',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      fontSize: '3.75rem',
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: '4.5rem',
    },
  },
  description: {
    fontSize: '1.125rem',
    lineHeight: 1.7,
    color: '#616161',
    fontWeight: 500,
    maxWidth: '672px',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  ctaButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  button: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },
  },
  trustBadge: {
    fontSize: '0.875rem',
    color: '#616161',
  },
  trustedBy: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#121212',
    marginTop: theme.spacing(3),
    fontWeight: 600,
  },
  imageContainer: {
    animation: '$fadeIn 0.6s ease-out',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(4),
      marginTop: '-80px',
    },
  },
  imageWrapper: {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  },
  video: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: '16px',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export default function Hero() {
  const classes = useStyles();
  const { geoData } = useGeoData();

  const handleCreateNowClick = () => {
    window.location.href = getRedirectUrlAfterLogin();
  };

  const handleGoogleSignUpClick = () => {
    window.location.href = getGoogleRedirectLink(geoData);
  };

  return (
    <section className={classes.section}>
      <Container className={classes.container}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Box className={classes.leftContent}>
              <Box>
                <Typography variant="h1" className={classes.title}>
                  Free Online Science Diagram Maker
                </Typography>
                <Typography className={classes.description}>
                  Create professional science diagrams for your research papers, grant proposals,
                  and conference presentations using our intuitive scientific diagram maker with
                  75,000+ scientifically accurate illustrations across 80+ disciplines.
                </Typography>
              </Box>

              <Box className={classes.ctaButtons}>
                <Button
                  size="large"
                  variant="contained"
                  className={classes.button}
                  onClick={handleCreateNowClick}
                  component="a"
                >
                  Create Now – It's Free
                </Button>
                <Button
                  size="large"
                  variant="text"
                  className={classes.button}
                  onClick={handleGoogleSignUpClick}
                  component="a"
                >
                  Sign Up with Google
                </Button>
              </Box>

              <Box>
                <Typography className={classes.trustBadge}>
                  No credit card required. Start creating in minutes.
                </Typography>
                <Typography className={classes.trustedBy}>
                  Trusted by 500K+ researchers worldwide
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box className={classes.imageContainer}>
              <Box className={classes.imageWrapper}>
                <video
                  src="/videos/videos-switcher/microbiology-video.webm"
                  alt="Scientific diagram maker video demonstration"
                  className={classes.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </section>
  );
}
